import React from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  FormGroup,
  Checkbox,
} from "@mui/material";
import { useEditLineCrossingsStore } from "../editLineCrossingsStore/editLineCrossingsStore";
import type { LineDirection } from "../editLineCrossingsStore/editLineCrossingsStore";

export const DirectionSelector = () => {
  const {
    lines,
    selectedLineIndex,
    updateLineDirection,
    metricLabels,
    updateLineMetricLabels,
    lineCrossingActive,
  } = useEditLineCrossingsStore();

  const selectedLine =
    selectedLineIndex !== null ? lines[selectedLineIndex] : null;
  // Only show a direction if a line is actually selected
  const currentDirection = selectedLine?.direction || "";
  const currentMetricLabels = selectedLine?.metric_labels || [];
  // Get the selected line's color, default to orange if no line selected
  const lineColor = selectedLine?.color || "#ed6c02";

  const handleDirectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newDirection = event.target.value as LineDirection;
    if (selectedLineIndex !== null) {
      updateLineDirection(selectedLineIndex, newDirection);
    }
  };

  // Separate intruder from vehicle/bike metrics
  const intruderMetric = metricLabels.find(
    (m) => m.label_name === "intruder_line_crossing"
  );
  const vehicleMetric = metricLabels.find(
    (m) => m.label_name === "vehicle_line_crossing"
  );
  const bikeMetric = metricLabels.find(
    (m) => m.label_name === "bike_line_crossing"
  );

  // Check if vehicle/bike combined checkbox should be checked (either one is checked)
  const isVehicleBikeChecked =
    (vehicleMetric && currentMetricLabels.includes(vehicleMetric.id)) ||
    (bikeMetric && currentMetricLabels.includes(bikeMetric.id));

  const handleMetricChange = (metricId: number, checked: boolean) => {
    if (selectedLineIndex === null) return;

    let newMetricLabels: number[];
    if (checked) {
      // Add the metric if it's not already present
      newMetricLabels = [...currentMetricLabels, metricId];
    } else {
      // Remove the metric, but prevent unchecking if it's the last one
      newMetricLabels = currentMetricLabels.filter((id) => id !== metricId);
      // Ensure at least one metric remains selected
      if (newMetricLabels.length === 0) {
        return; // Don't allow unchecking the last metric
      }
    }
    updateLineMetricLabels(selectedLineIndex, newMetricLabels);
  };

  // Handle combined Vehicle/Bike checkbox
  const handleVehicleBikeChange = (checked: boolean) => {
    if (selectedLineIndex === null) return;

    let newMetricLabels = [...currentMetricLabels];

    if (checked) {
      // Add both vehicle and bike IDs if they exist and aren't already present
      if (vehicleMetric && !newMetricLabels.includes(vehicleMetric.id)) {
        newMetricLabels.push(vehicleMetric.id);
      }
      if (bikeMetric && !newMetricLabels.includes(bikeMetric.id)) {
        newMetricLabels.push(bikeMetric.id);
      }
    } else {
      // Remove both vehicle and bike IDs, but prevent if it would leave no metrics
      const vehicleId = vehicleMetric?.id;
      const bikeId = bikeMetric?.id;
      const potentialNewLabels = newMetricLabels.filter(
        (id) => id !== vehicleId && id !== bikeId
      );
      // Ensure at least one metric remains selected
      if (potentialNewLabels.length === 0) {
        return; // Don't allow unchecking if it would remove all metrics
      }
      newMetricLabels = potentialNewLabels;
    }
    updateLineMetricLabels(selectedLineIndex, newMetricLabels);
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "background.paper",
        minWidth: "250px",
        height: "fit-content",
      }}
    >
      <FormControl component="fieldset" fullWidth>
        <FormLabel
          component="legend"
          sx={{
            fontFamily: "Mulish",
            fontSize: "16px",
            fontWeight: "bold",
            color: "black !important",
            "&.Mui-focused": {
              color: "black !important",
            },
            "&:hover": {
              color: "black !important",
            },
          }}
        >
          Line Crossing Direction
        </FormLabel>
        <RadioGroup
          value={currentDirection}
          onChange={handleDirectionChange}
          row
        >
          <FormControlLabel
            value="AtoB"
            control={
              <Radio
                sx={{
                  color: "black",
                  "&.Mui-checked": {
                    color: lineColor,
                  },
                }}
              />
            }
            label="A to B"
            disabled={selectedLineIndex === null}
            sx={{
              fontFamily: "Mulish",
              "& .MuiFormControlLabel-label": {
                fontFamily: "Mulish",
                color: "black",
              },
              "&.Mui-disabled .MuiFormControlLabel-label": {
                color: "rgba(0, 0, 0, 0.26) !important",
              },
            }}
          />
          <FormControlLabel
            value="BtoA"
            control={
              <Radio
                sx={{
                  color: "black",
                  "&.Mui-checked": {
                    color: lineColor,
                  },
                }}
              />
            }
            label="B to A"
            disabled={selectedLineIndex === null}
            sx={{
              fontFamily: "Mulish",
              "& .MuiFormControlLabel-label": {
                fontFamily: "Mulish",
                color: "black",
              },
              "&.Mui-disabled .MuiFormControlLabel-label": {
                color: "rgba(0, 0, 0, 0.26) !important",
              },
            }}
          />
          <FormControlLabel
            value="Both"
            control={
              <Radio
                sx={{
                  color: "black",
                  "&.Mui-checked": {
                    color: lineColor,
                  },
                }}
              />
            }
            label="Both"
            disabled={selectedLineIndex === null}
            sx={{
              fontFamily: "Mulish",
              "& .MuiFormControlLabel-label": {
                fontFamily: "Mulish",
                color: "black",
              },
              "&.Mui-disabled .MuiFormControlLabel-label": {
                color: "rgba(0, 0, 0, 0.26) !important",
              },
            }}
          />
        </RadioGroup>
        {selectedLineIndex === null && (
          <Box
            sx={{
              mt: 1,
              fontSize: "0.875rem",
              color: "text.secondary",
              fontFamily: "Mulish",
            }}
          >
            Select a line to change its direction. Or click anywhere on the
            image to start a new line.
          </Box>
        )}
      </FormControl>

      {/* Metric Labels Section */}
      <FormControl component="fieldset" fullWidth sx={{ mt: 3 }}>
        <FormLabel
          component="legend"
          sx={{
            fontFamily: "Mulish",
            fontSize: "16px",
            fontWeight: "bold",
            color: "black !important",
            "&.Mui-focused": {
              color: "black !important",
            },
            "&:hover": {
              color: "black !important",
            },
          }}
        >
          Detection Types
        </FormLabel>
        <FormGroup>
          {/* Intruder checkbox - only show if intruder line crossing is active */}
          {intruderMetric && lineCrossingActive.intruder && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentMetricLabels.includes(intruderMetric.id)}
                  onChange={(e) =>
                    handleMetricChange(intruderMetric.id, e.target.checked)
                  }
                  disabled={
                    selectedLineIndex === null ||
                    (currentMetricLabels.includes(intruderMetric.id) &&
                      currentMetricLabels.length === 1)
                  }
                  sx={{
                    color: "black",
                    "&.Mui-checked": {
                      color: lineColor,
                    },
                  }}
                />
              }
              label="Intruder"
              sx={{
                fontFamily: "Mulish",
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Mulish",
                  color: "black",
                },
                "&.Mui-disabled .MuiFormControlLabel-label": {
                  color: "rgba(0, 0, 0, 0.26) !important",
                },
              }}
            />
          )}
          {/* Combined Vehicle / Bike checkbox - only show if vehicle line crossing is active */}
          {(vehicleMetric || bikeMetric) && lineCrossingActive.vehicle && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!isVehicleBikeChecked}
                  onChange={(e) => handleVehicleBikeChange(e.target.checked)}
                  disabled={
                    selectedLineIndex === null ||
                    (isVehicleBikeChecked &&
                      (!intruderMetric || !lineCrossingActive.intruder)) ||
                    (isVehicleBikeChecked &&
                      intruderMetric &&
                      lineCrossingActive.intruder &&
                      !currentMetricLabels.includes(intruderMetric.id))
                  }
                  sx={{
                    color: "black",
                    "&.Mui-checked": {
                      color: lineColor,
                    },
                  }}
                />
              }
              label="Vehicle / Bike"
              sx={{
                fontFamily: "Mulish",
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Mulish",
                  color: "black",
                },
                "&.Mui-disabled .MuiFormControlLabel-label": {
                  color: "rgba(0, 0, 0, 0.26) !important",
                },
              }}
            />
          )}
        </FormGroup>
        {selectedLineIndex === null &&
          metricLabels.length > 0 &&
          (lineCrossingActive.intruder || lineCrossingActive.vehicle) && (
            <Box
              sx={{
                mt: 1,
                fontSize: "0.875rem",
                color: "text.secondary",
                fontFamily: "Mulish",
              }}
            >
              Select a line to change its detection types, or click anywhere on
              the image to start a new line.
            </Box>
          )}
        {(metricLabels.length === 0 ||
          (!lineCrossingActive.intruder && !lineCrossingActive.vehicle)) && (
          <Box
            sx={{
              mt: 1,
              fontSize: "0.875rem",
              color: "text.secondary",
              fontFamily: "Mulish",
            }}
          >
            No detection types available for this camera
          </Box>
        )}
        {metricLabels.length > 0 &&
          selectedLineIndex !== null &&
          (lineCrossingActive.intruder || lineCrossingActive.vehicle) && (
            <Box
              sx={{
                mt: 1,
                fontSize: "0.75rem",
                color: "text.secondary",
                fontFamily: "Mulish",
              }}
            >
              At least one detection type must be selected
            </Box>
          )}
      </FormControl>
    </Box>
  );
};
